import React from "react";
import { IDevice, IFirmware, TDeviceType } from "./types";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import useCreateData from "../../hooks/useCreateData";
import useUpdateData from "../../hooks/useUpdateData";
import { TextField, Stack, FormControlLabel, Switch } from "@mui/material";
import FormikSelect, { TOption } from "../../Components/FormikSelect";
import DialogButtons from "../../Components/DialogButtons";
import { getFirmwareOptions } from "./utils";
import { useTranslation } from "react-i18next";

const deviceTypeOptions: TOption<TDeviceType>[] = [
  { value: "BIOLYT", label: "Biolyt" },
  { value: "BLINK", label: "Blink" },
  { value: "BOSCH_DEVICE", label: "Bosch Device" },
  { value: "CHICKEN", label: "Chicken" },
  { value: "CLIMATE", label: "Climate" },
  { value: "COMFOAIR350", label: "Comfoair 350" },
  { value: "LED_STRIPE", label: "LED Stripe" },
  { value: "MIELE_DEVICE", label: "Miele Device" },
  { value: "PHOTO_VOLTAIC", label: "Photo Voltaic" },
  { value: "SHELLY_1", label: "Shelly 1" },
  { value: "SHELLY_1_MINI_GEN3", label: "Shelly 1 mini Gen3" },
  { value: "SHELLY_25", label: "Shelly 25" },
  { value: "SHELLY_PLUG_S", label: "Shelly Plug S" },
  { value: "SOUND", label: "Sound" },
];

interface IDeviceFormProps {
  device: IDevice;
  firmwares: IFirmware[];
}

const DeviceForm = (props: IDeviceFormProps) => {
  const { device, firmwares } = props;
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text: device.name
        ? t("Editing device DEVICE cancelled", { device })
        : t("Creating new device cancelled"),
      severity: "info",
    });
    navigate("/devices");
  };

  const createHandler = useCreateData();
  const updateHandler = useUpdateData();
  const formik = useFormik({
    initialValues: {
      name: device.name,
      device_id: device.device_id,
      device_type: device.device_type,
      is_alive_check_enabled: device.is_alive_check_enabled,
      firmware_id: device.firmware_id,
      debug_logging: device.data.debug_logging,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("Required")),
      device_id: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const allValues = { ...device, ...values };
      if (device.id === -1) {
        createHandler(
          t("Device WHAT", { what: allValues.name }),
          "/devices",
          allValues
        );
      } else {
        updateHandler(
          t("Device WHAT", { what: allValues.name }),
          `/devices/${device.id}`,
          allValues
        );
      }
      navigate("/devices");
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} data-cy="device-form">
      <FormikProvider value={formik}>
        <Stack spacing={2} alignItems="flex-start">
          <TextField
            fullWidth
            autoFocus
            margin="normal"
            id="name"
            label={t("Name")}
            type="text"
            disabled={isSubmitting}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
          ></TextField>
          <FormikSelect
            fullWidth
            label={t("Device Type")}
            name="device_type"
            options={deviceTypeOptions}
            margin="none"
            data-cy="device-select-device-type"
          />
          <TextField
            fullWidth
            autoFocus
            margin="normal"
            id="device_id"
            label={t("Device ID")}
            type="text"
            disabled={isSubmitting}
            error={formik.touched.device_id && Boolean(formik.errors.device_id)}
            helperText={formik.touched.device_id && formik.errors.device_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.device_id}
          ></TextField>
          <FormControlLabel
            control={
              <Switch
                id="is_alive_check_enabled"
                name="is_alive_check_enabled"
                checked={formik.values.is_alive_check_enabled}
                onChange={formik.handleChange}
              />
            }
            label={t("Alive Check enabled")}
            labelPlacement="start"
          />
          {device.firmware_id && (
            <FormikSelect
              fullWidth
              label="Firmware"
              name="firmware_id"
              options={getFirmwareOptions(firmwares, device.device_type)}
              margin="none"
              data-cy="device-select-firmware"
            />
          )}
          {device.data.debug_logging && (
            <FormControlLabel
              control={
                <Switch
                  id="debug_logging"
                  name="debug_logging"
                  checked={formik.values.debug_logging === "on"}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "debug_logging",
                      e.target.checked ? "on" : "off"
                    );
                  }}
                />
              }
              label={t("Debug logging enabled")}
              labelPlacement="start"
            />
          )}
        </Stack>
        <DialogButtons
          isSubmitting={isSubmitting}
          submitButtonLabel="Save"
          resetCallback={handleCancel}
        />
      </FormikProvider>
    </form>
  );
};

export default DeviceForm;
